// Data sources for ∑: Variants, Sales History and the Daily NERV Trends chart
// (FIG-345 · UC-229). The option lists and smart defaults were moved here
// unchanged from OrganisationReportsTab and DailyNervView, so the Settings tab
// offers exactly what the report and chart can use. A saved choice
// (organisations.report_data_sources) is applied with the resolvers below; when
// nothing is saved, or the saved source is gone, today's smart default applies.
import {
  detectEcommercePlatform,
  getCsvEcommerceSubtype,
  isReportsSelectableConnection,
  type ConnectionPlatformInput,
  type CsvConnectionInput,
} from './connectionPlatform';
import { dailyNervSourceFor, type ReportSourceChoice, type ReportSourceKey } from './types/reportDataSources';
import { buildReportSourceOptionsWith, type ReportSourceOption, type ResolvedOrigin } from './reportSourceOptions';

export {
  defaultReportSourceIds,
  describeReportSourceIds,
  resolveReportSources,
  selectableReportSources,
  type ReportSourceOption,
  type ResolvedOrigin,
  type ResolvedReportSources,
} from './reportSourceOptions';

export interface DailyNervSourceOption {
  value: string;
  label: string;
}

type ConnectionLike = ConnectionPlatformInput & CsvConnectionInput & {
  id?: string | null;
  connectionId?: string | null;
  name?: string | null;
};

/**
 * The report's source list: one option per reportable connection, an "All"
 * entry when there are several, or a single 'none' entry when there are none.
 */
export function buildReportSourceOptions(connections: unknown): ReportSourceOption[] {
  return buildReportSourceOptionsWith(
    { isReportsSelectableConnection, detectEcommercePlatform, getCsvEcommerceSubtype },
    connections,
  );
}

const connectionList = (connections: unknown): ConnectionLike[] => {
  if (!connections || typeof connections !== 'object') return [];
  return (Array.isArray(connections) ? connections : Object.values(connections)) as ConnectionLike[];
};

/** Which saved row a report tab reads. Fixed Costs keeps sharing the Variants source, as it shared the report's selector before. */
export const reportSourceKeyForTab = (tab: string): ReportSourceKey =>
  tab === 'sales-history' ? 'salesHistory' : 'variants';

/** The Daily NERV Trends chart's source list (platform values, plus "All Sources"). */
export function buildDailyNervSourceOptions(connections: unknown): DailyNervSourceOption[] {
  const sources: DailyNervSourceOption[] = [{ value: 'All', label: 'All Sources' }];
  for (const conn of connectionList(connections)) {
    const option = dailyNervSourceFor(conn);
    if (option && !sources.find((s) => s.value === option.value)) sources.push(option);
  }
  return sources;
}

/** The chart's smart default: the API platform when both API and CSV exist, else All. */
export function defaultDailyNervSource(connections: unknown): string {
  const list = connectionList(connections);
  if (list.length === 0) return 'All';

  const apiPlatforms = new Set<string>();
  const csvPlatforms = new Set<string>();
  for (const conn of list) {
    if (!conn || conn.service === 'file') continue;
    if (conn.type === 'CSV' || conn.connectionId?.startsWith('csv-import-')) {
      if (conn.ecommerce_subtype) csvPlatforms.add(conn.ecommerce_subtype.toLowerCase());
    } else {
      const service = (conn.service || '').toLowerCase();
      if (service.includes('amazon')) apiPlatforms.add('amazon');
      else if (service.includes('shopify')) apiPlatforms.add('shopify');
    }
  }
  for (const platform of apiPlatforms) {
    if (csvPlatforms.has(platform)) return platform;
  }
  return 'All';
}

export interface ResolvedDailyNervSource {
  value: string;
  label: string;
  origin: ResolvedOrigin;
}

export function resolveDailyNervSource(
  choice: ReportSourceChoice | undefined,
  connections: unknown,
): ResolvedDailyNervSource {
  const options = buildDailyNervSourceOptions(connections);
  if (choice?.kind === 'all') return { value: 'All', label: 'All Sources', origin: 'saved' };
  if (choice?.kind === 'source') {
    const match = options.find((o) => o.value === choice.id && o.value !== 'All');
    if (match) return { value: match.value, label: match.label, origin: 'saved' };
  }
  const value = defaultDailyNervSource(connections);
  const label = options.find((o) => o.value === value)?.label ?? 'All Sources';
  return { value, label, origin: choice ? 'fallback' : 'default' };
}

export interface ResolvedDailyNervSources {
  /** Chart source values to filter on; empty means All Sources (no filter). */
  values: string[];
  label: string;
  origin: ResolvedOrigin;
  /** Saved values no longer offered and left out (FIG-409 · UC-234). */
  missingCount?: number;
}

/**
 * The Daily NERV Trends sources for a saved choice (FIG-409 · UC-234). 'all'
 * and 'source' resolve exactly as resolveDailyNervSource; 'sources' keeps the
 * values still offered and falls back to the default when none are left.
 */
export function resolveDailyNervSources(
  choice: ReportSourceChoice | undefined,
  connections: unknown,
): ResolvedDailyNervSources {
  if (choice?.kind === 'sources') {
    const offered = buildDailyNervSourceOptions(connections).filter((o) => o.value !== 'All');
    const kept = offered.filter((o) => choice.ids.includes(o.value));
    if (kept.length > 0) {
      const values = kept.map((o) => o.value);
      const missingCount = new Set(choice.ids).size - kept.length;
      // Never "All Sources" for an explicit selection (Cursor Bugbot on #360).
      const label = kept.length === 1 ? kept[0].label : `${kept.length} sources selected`;
      return { values, label, origin: 'saved', missingCount };
    }
    const value = defaultDailyNervSource(connections);
    const fallback = resolveDailyNervSource(value === 'All' ? { kind: 'all' } : { kind: 'source', id: value }, connections);
    return { values: fallback.value === 'All' ? [] : [fallback.value], label: fallback.label, origin: 'fallback', missingCount: new Set(choice.ids).size };
  }
  const single = resolveDailyNervSource(choice, connections);
  return { values: single.value === 'All' ? [] : [single.value], label: single.label, origin: single.origin };
}
